import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, tap, timeout } from 'rxjs/operators';
import { DataRulesService } from 'src/app/data-rules/data-rules.service';
import { DuplicateService } from 'src/app/duplikit/duplicate.service';
import { environment } from '../../environments/environment';
import { CandidateOptionsService } from '../data-cleaner/candidate-options/candidate-options.service';
import { FeatureFlagService } from '../feature-flags/feature-flag.service';
import { DataToolsApps } from '../shared/DataToolsApps';
import { IDataToolsUser, IUserInfo } from '../shared/interfaces/user';
import DataToolsOption from './data-tools-option';

@Injectable()
export class AdminService {
  corporationName: EventEmitter<string> = new EventEmitter();
  userInfo: EventEmitter<IUserInfo> = new EventEmitter();
  dataRulesMaxNumberOfAutomatedRules: number;
  private finishedLoadingSubject = new BehaviorSubject<boolean>(false);
  public finishedLoading$ = this.finishedLoadingSubject.asObservable();
  private finishedCheckingAppsSubject = new Subject<boolean>();
  public finishedCheckingApps$ = this.finishedCheckingAppsSubject.asObservable();
  private dataToolsOptions: DataToolsOption[] = [];
  private appCheckingSubjects = new Map<DataToolsApps, BehaviorSubject<boolean>>();
  private user: IDataToolsUser = {authorizedUser: true, adminUser: false};
  private readonly TIMEOUT_DURATION: number = 10000;



  private http: HttpClient;
  private duplicateService: DuplicateService;
  private candidateOptionsService: CandidateOptionsService;
  private dataRulesService: DataRulesService;
  private featureFlagService: FeatureFlagService

  constructor(http: HttpClient,
              duplicateService: DuplicateService,
              candidateOptionsService: CandidateOptionsService,
              dataRulesService: DataRulesService,
              featureFlagService: FeatureFlagService) {
    this.http = http;
    this.duplicateService = duplicateService;
    this.candidateOptionsService = candidateOptionsService;
    this.dataRulesService = dataRulesService;
    this.featureFlagService = featureFlagService;
    const corpUrl = `${environment.dataToolsUrl}/corporation`;
    this.dataToolsOptions[DataToolsApps.DUPLIKIT] = new DataToolsOption(DataToolsApps.DUPLIKIT, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.DATA_CLEANER] = new DataToolsOption(DataToolsApps.DATA_CLEANER, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.DATARULES] = new DataToolsOption(DataToolsApps.DATARULES, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.USER_MANAGEMENT] = new DataToolsOption(DataToolsApps.USER_MANAGEMENT, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.DATARULES_SCHEDULING] = new DataToolsOption(DataToolsApps.DATARULES_SCHEDULING, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.DATARULES_CREDENTIALLING] = new DataToolsOption(DataToolsApps.DATARULES_CREDENTIALLING, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.DATARULES_AI_ASSIST] = new DataToolsOption(DataToolsApps.DATARULES_AI_ASSIST, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.DATARULES_ENRICHMENT] = new DataToolsOption(DataToolsApps.ENRICHMENT_SETTINGS, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.DATARULES_CHAINED_RULES] = new DataToolsOption(DataToolsApps.DATARULES_CHAINED_RULES, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.DATATOOLS_LITE] = new DataToolsOption(DataToolsApps.DATATOOLS_LITE, corpUrl, false);
    this.dataToolsOptions[DataToolsApps.DATARULES_BULLHORN_ONE_ENTITIES] = new DataToolsOption(DataToolsApps.DATARULES_BULLHORN_ONE_ENTITIES, corpUrl, false);
  }

  loadApps() {
    this.http.get(`${environment.dataToolsUrl}/userInfo`)
      .pipe(
        switchMap((res: IUserInfo) => {
          this.userInfo.emit(res);
          return this.featureFlagService.init({
            environment: environment.name,
            tenant_id: res.corpId.toString(),
            user_id: res.userId?.toString(),
          });
        }),
        switchMap(() => {
          return this.http.get(`${environment.dataToolsUrl}/corporation`);
        }))
      .subscribe((res: any) => {
        this.corporationName.emit(res.name);
        this.dataToolsOptions[DataToolsApps.DATARULES_SCHEDULING].enabled = res[DataToolsApps.DATARULES_SCHEDULING];
        this.dataToolsOptions[DataToolsApps.DATARULES_CREDENTIALLING].enabled = res[DataToolsApps.DATARULES_CREDENTIALLING];
        this.dataToolsOptions[DataToolsApps.DATARULES_CHAINED_RULES].enabled = res[DataToolsApps.DATARULES_CHAINED_RULES];
        this.dataToolsOptions[DataToolsApps.DATARULES_AI_ASSIST].enabled = res[DataToolsApps.DATARULES_AI_ASSIST];
        this.dataToolsOptions[DataToolsApps.DATARULES_ENRICHMENT].enabled = res[DataToolsApps.DATARULES_ENRICHMENT];
        this.dataRulesMaxNumberOfAutomatedRules = res.datarulesMaxNumberOfAutomatedRules;
        this.dataToolsOptions[DataToolsApps.DATATOOLS_LITE].enabled = res[DataToolsApps.DATATOOLS_LITE];
        this.dataToolsOptions[DataToolsApps.DATARULES_BULLHORN_ONE_ENTITIES].enabled = res[DataToolsApps.DATARULES_BULLHORN_ONE_ENTITIES];
        [DataToolsApps.DUPLIKIT, DataToolsApps.DATA_CLEANER, DataToolsApps.DATARULES]
          .filter(app => res[app])
          .forEach(app => this.setAppChecking(app, true));
        this.finishedCheckingAppsSubject.next(true);
        this.checkUserPermissions(res);
      });
  }

  checkUserPermissions(enabledApps: any): void {
    const allChecks: Observable<any>[] = [];

    if (enabledApps[DataToolsApps.DUPLIKIT]) {
      this.setAppChecking(DataToolsApps.DUPLIKIT, true);
      allChecks.push(
        this.duplicateService.checkAuth().pipe(
          timeout(this.TIMEOUT_DURATION),
          tap(() => { this.enableApp(DataToolsApps.DUPLIKIT); this.setAppChecking(DataToolsApps.DUPLIKIT, false); }),
          catchError(error => {
            if (error.status === 403) { this.disableApp(DataToolsApps.DUPLIKIT); }
            this.setAppChecking(DataToolsApps.DUPLIKIT, false);
            return of(null);
          })
        )
      );

      allChecks.push(
        this.duplicateService.checkAdminUser().pipe(
          timeout(this.TIMEOUT_DURATION),
          tap(() => this.user.adminUser = true),
          catchError(() => of(null))
        )
      );
    }

    if (enabledApps[DataToolsApps.DATA_CLEANER]) {
      this.setAppChecking(DataToolsApps.DATA_CLEANER, true);
      allChecks.push(
        this.candidateOptionsService.checkAuth().pipe(
          timeout(this.TIMEOUT_DURATION),
          tap(() => { this.enableApp(DataToolsApps.DATA_CLEANER); this.setAppChecking(DataToolsApps.DATA_CLEANER, false); }),
          catchError(error => {
            if (error.status === 403) { this.disableApp(DataToolsApps.DATA_CLEANER); }
            this.setAppChecking(DataToolsApps.DATA_CLEANER, false);
            return of(null);
          })
        )
      );

      allChecks.push(
        this.candidateOptionsService.checkAdminUser().pipe(
          timeout(this.TIMEOUT_DURATION),
          tap(() => this.user.adminUser = true),
          catchError(() => of(null))
        )
      );
    }

    if (enabledApps[DataToolsApps.DATARULES]) {
      this.setAppChecking(DataToolsApps.DATARULES, true);
      allChecks.push(
        this.dataRulesService.checkAuth().pipe(
          timeout(this.TIMEOUT_DURATION),
          tap(() => { this.enableApp(DataToolsApps.DATARULES); this.setAppChecking(DataToolsApps.DATARULES, false); }),
          catchError(error => {
            if (error.status === 403) { this.disableApp(DataToolsApps.DATARULES); }
            this.setAppChecking(DataToolsApps.DATARULES, false);
            return of(null);
          })
        )
      );

      allChecks.push(
        this.dataRulesService.checkAdminUser().pipe(
          timeout(this.TIMEOUT_DURATION),
          tap(() => this.user.adminUser = true),
          catchError(() => of(null))
        )
      );
    }

    forkJoin(allChecks.length ? allChecks : [of(null)]).subscribe(() => {
      this.user.authorizedUser = true;
      this.finishedLoadingSubject.next(true);
    });
  }

  isAppChecking$(app: DataToolsApps): Observable<boolean> {
    if (!this.appCheckingSubjects.has(app)) {
      this.appCheckingSubjects.set(app, new BehaviorSubject<boolean>(false));
    }
    return this.appCheckingSubjects.get(app).asObservable();
  }

  private setAppChecking(app: DataToolsApps, checking: boolean): void {
    if (!this.appCheckingSubjects.has(app)) {
      this.appCheckingSubjects.set(app, new BehaviorSubject<boolean>(false));
    }
    this.appCheckingSubjects.get(app).next(checking);
  }

  enableApp(app: DataToolsApps) {
    this.dataToolsOptions[app].enabled = true;
    this.user.authorizedUser = true;
  }

  disableApp(app: DataToolsApps) {
    this.dataToolsOptions[app].enabled = false;
    this.user.authorizedUser = false;
  }

  get userStatus(): IDataToolsUser {
    return this.user;
  }

  checkAppEnabled(app: DataToolsApps): boolean {
    return this.dataToolsOptions[app].enabled;
  }

  getCorporationData(): Observable<Object> {
    return this.http.get(`${environment.dataToolsUrl}/corporation`);
  }

  get maxNumberOfAutomatedRules() {
    return this.dataRulesMaxNumberOfAutomatedRules;
  }

  saveOptions(dataCleaner: boolean, duplikit: boolean, config: boolean, user: boolean) {
    // TODO Use DataToolsApps.ts constants to describe "appName"'s
    this.http.post('admin/saveOptions', {
      config: {id: 0, enabled: config, appName: 'Config'},
      dataCleaner: {id: 1, enabled: dataCleaner, appName: 'DataCleaner'},
      duplikit: {id: 2, enabled: duplikit, appName: 'Duplikit'},
      user: {id: 3, enabled: user, appName: 'User'}
    }).subscribe(() => {
    });
  }

  isInLiteMode(): boolean {
    return this.dataToolsOptions[DataToolsApps.DATATOOLS_LITE].enabled;
  }
}
